import {computed} from 'vue'
import {defineStore} from 'pinia'
import {useStore} from 'shared/stores/store'

export const useWorkspaceSubscriptionStore = defineStore('workspaceSubscription', () => {
	const store = useStore()

	const activePlanTrial = computed(() => store.workspace?.activePlanTrial?.[0] || {})

	const plan = computed(() => store.workspace?.plan?.[0] || {})

	const subscription = computed(() => store.workspace?.subscription?.[0] || {})

	const isFreePlan = computed(() => plan.value?.type === 'free')
	const isEnterprisePlan = computed(() => plan.value?.type === 'enterprise')
	const isFreemiumPlan = computed(() => plan.value?.type === 'freemium' && subscription.value?.status === 'active')
	const isTrialPlan = computed(() => Boolean(activePlanTrial.value?.planId))
	const isActive = computed(() => Boolean(subscription.value?.status === 'active'))
	const isCancelled = computed(
		() =>
			subscription.value?.status === 'cancelled' &&
			new Date(subscription.value?.cancellationEffectiveAt) <= new Date()
	)
	const isPaused = computed(() => subscription.value?.status === 'paused')

	const trialDaysLeft = computed(() => {
		if (!isTrialPlan.value) return undefined
		const daysLeft = activePlanTrial.value?.daysLeft
		return daysLeft !== undefined ? parseInt(daysLeft) : daysLeft
	})

	const hasTrialEnded = computed(() => {
		if (!isTrialPlan.value) return false
		if (trialDaysLeft.value === undefined) return false
		return trialDaysLeft.value < 0
	})

	const problem = computed(() => {
		// if the subscription is active, there is no problem
		if (isActive.value) return null
		// if there is a trial plan, check for possible issues, otherwise return no problem
		if (isTrialPlan.value) {
			if (trialDaysLeft.value === undefined) return null
			if (trialDaysLeft.value < 0) return 'trial-ended'
			return null
		}
		// rest of the conditions
		if (isFreePlan.value) return 'free-plan-ended'
		if (isCancelled.value) return 'subscription-ended'
		if (isPaused.value) return 'subscription-paused'
		return null
	})

	function getProblem({activePlanTrial, plan, subscription}) {
		activePlanTrial = activePlanTrial?.[0] || {}
		plan = plan?.[0] || {}
		subscription = subscription?.[0] || {}

		// if the subscription is active, there is no problem
		if (subscription.status === 'active') return null

		// if there is a trial plan, check for possible issues, otherwise return no problem
		const isTrialPlan = Boolean(activePlanTrial.planId)
		if (isTrialPlan) {
			const trialDaysLeft =
				activePlanTrial.daysLeft !== undefined ? parseInt(activePlanTrial.daysLeft) : undefined
			if (trialDaysLeft === undefined) return null
			if (trialDaysLeft < 0) return 'trial-ended'
			return null
		}

		// rest of the conditions
		if (plan.type === 'free') return 'free-plan-ended'
		if (subscription.status === 'cancelled' && new Date(subscription.cancellationEffectiveAt) <= new Date()) {
			return 'subscription-ended'
		}
		if (subscription.status === 'paused') return 'subscription-paused'

		return null
	}

	return {
		activePlanTrial,
		plan,
		subscription,
		isTrialPlan,
		trialDaysLeft,
		hasTrialEnded,
		isFreePlan,
		isFreemiumPlan,
		isEnterprisePlan,
		isCancelled,
		isPaused,
		problem,
		getProblem,
		status: computed(() => subscription.value?.status),
		platform: computed(() => subscription.value?.platform),
		isActive: computed(() => subscription.value?.status === 'active' && !subscription.value?.pausedAt),
		isPastDue: computed(() => subscription.value?.status === 'past_due'),
		willBePaused: computed(() => subscription.value?.status === 'active' && subscription.value?.pausedAt),
		willBeCancelled: computed(
			() =>
				subscription.value?.status === 'cancelled' &&
				new Date(subscription.value?.cancellationEffectiveAt) > new Date()
		),
		planType: computed(() => plan.value?.type),
		usageBased: computed(() => plan.value?.usageBased),
		isMonthlyPlan: computed(() => plan.value?.billingFrequency === 'month'),
		isAnnualPlan: computed(() => plan.value?.billingFrequency === 'year'),
		allowedRoles: computed(() => plan.value?.allowedRoles || ['admin']),
		maxIntegrationCount: computed(() => plan.value?.maxIntegrationCount || 0),
		isIntegrationAllowed: computed(() => plan.value?.maxIntegrationCount > 0),
		maxCollectionCount: computed(() => plan.value?.maxCollectionCount || Infinity),
		maxMonthlySessionCount: computed(() => plan.value?.maxMonthlySessionCount || Infinity),
		allowsFolderAccessManagement: computed(() => ['premium', 'enterprise'].includes(plan.value?.type)),
	}
})
